import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, interval, Observable, Subject, Subscription, throwError } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Alert } from "../classes/alert";
import { AlertDataForNativeMobile } from "../classes/alert-native-mobile";
import { AppConfiguration } from "../classes/configuration";
import { Profile } from "../classes/profile";
import { ALERT_WORKER_TIMER } from "../common/constants";
import { ConfigurationService } from "./configuration.service";
import { LoggerService } from "./logger.service";
import { MsalConfigService } from './msal-config.service';
import { ProfileService } from "./profile.service";
import { ToastService } from "./toast.service";

export interface MeatBallData{
  coordinator: number,
  driver: number
}

const useImMemoryWebApi = !environment.production && environment.inMemoryWebApiEnabled;
const worker = new Worker(new URL("../workers/alert.worker", import.meta.url));
@Injectable({
  providedIn: "root",
})
export class AlertService {
  private alertData$: BehaviorSubject<Array<Alert>> = new BehaviorSubject([]);
  alertForNativeMobile$: BehaviorSubject<AlertDataForNativeMobile> = new BehaviorSubject(null);
  private meatballData$: BehaviorSubject<MeatBallData> = new BehaviorSubject(null);
  unsubscribe$: Subject<boolean> = new Subject();
  alertsPayload = new Array<number>();
  alertsURL: string;
  configuration: AppConfiguration;
  profile: Profile;
  interval: Subscription = new Subscription();

  constructor(
    private logger: LoggerService,
    private configurationService: ConfigurationService,
    private profileService: ProfileService,
    private http: HttpClient,
    private msalConfigService: MsalConfigService,
    private toastService: ToastService
  ) {
    this.subscribeToConfiguration();
    this.subscribeToProfile();
  }

  subscribeToConfiguration(): void {
    this.configurationService.getConfiguration()
      .pipe(
        takeUntil(this.unsubscribe$))
      .subscribe((configuration) => {
        this.configuration = configuration;
        this.alertsURL = configuration.HostApi.concat('/alert');
      });
  }

  updateMeatballData(data: MeatBallData): void {
    this.meatballData$.next(data);
  }

  getMeatballData(): Observable<MeatBallData> {
    return this.meatballData$.asObservable();
  }

  updateAlertData(data: Array<Alert>): void {
    this.alertData$.next(data);
  }

  getAlertData(): Observable<Array<Alert>> {
    return this.alertData$.asObservable();
  }

  updateAlertForNativeMobile(data: AlertDataForNativeMobile): void {
    this.alertForNativeMobile$.next(data);
  }

  getAlertForNativeMobile(): Observable<AlertDataForNativeMobile> {
    return this.alertForNativeMobile$.asObservable();
  }

  // toggle API threading
  public startAlertInterval(): void {
    if (useImMemoryWebApi) {
      this.runMainThread();
    } else {
      this.runWorkerThread();
    }
    this.interval = interval(ALERT_WORKER_TIMER).subscribe(() => {
      if (useImMemoryWebApi) {
        this.runMainThread();
      } else {
        this.runWorkerThread();
      }
    });
  }

  public stopWorkerInterval(): void {
    this.interval.unsubscribe();
  }

  // Web Worker Thread PUT
  public runWorkerThread(): any {
    this.logger.debug("Alerts called on worker thread", 'worker');
    worker.onmessage = ({ data }) => {
      if (data.error) {
        this.logger.error(`Alert worker error: ${data.message} (status: ${data.status})`);
        return;
      }
      const parsed = JSON.parse(data.data);
      this.updateAlertData(parsed);
      return parsed;
    };
    worker.onerror = (err) => {
      this.logger.error('Alert worker uncaught error:', err);
    };
    this.msalConfigService.getAzureToken().subscribe((token: string) => {
      worker.postMessage({
        "url": this.alertsURL + '?driver=' + this.profile.adId,
        "timeout": null,
        "payload": JSON.stringify(this.getAlertsPayload()),
        "authHeader": `Bearer ${token}`
      });
    })
  }

  // Main Thread PUT(using GET for mock because it's a json file)... used when inMemoryWebApi = true
  public runMainThread(): void {
    this.logger.debug("Alerts called on main thread", 'worker');
    const headers = new HttpHeaders()
      .set("Content-type", "application/json; charset=utf-8")
    this.http.get<Array<any>>('assets/data/mock-data/alerts-response1.json', { 'headers': headers })
      .subscribe((data) => {
        this.updateAlertData(data);
        this.logger.debug(`PUT Alert response:`, data, 'http');
      });
  }

  getAlertsPayload(): Array<number> {
    return []; // // TODO get real payload for PUT
  }

  subscribeToProfile(): void {
    this.profileService.getProfileObs().pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile) => {
        if (profile) {
          this.profile = profile;
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  acknowledgeAlert(alertId: number): Observable<HttpResponse<any>>{
    const config = this.configurationService.configuration$.getValue();
    if (!config) {
      return throwError(new Error('Configuration not loaded'));
    }
    const url = config.HostApi.concat('/alert/').concat(alertId.toString());
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, {}, { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to acknowledge alert'))
    );
  }

  private handleError(error: HttpErrorResponse, message: string) {
    this.toastService.show('HTTP Error Occurred!', { classname: 'bg-danger text-light', delay: 8000 });
    if (error.status === 0) {
      this.logger.error(`${message}:`, error.error);
    } else {
      this.logger.error(`${message}: ${error.status}, body was: ${error.error}`);
    }
    return throwError(error);
  }

}
